import React, { useEffect, useRef, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Minimize2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useVoiceChat, useVoiceChatIntegration } from "../hooks";

// Ses dalgası bileşeni
const AudioWaveform = React.memo(
  ({ level, color = "green" }: { level: number; color?: "green" | "blue" }) => {
    const svgRef = useRef<SVGSVGElement>(null);

    // ✅ Level değişikliklerini throttle et (performans için)
    const throttledLevel = useMemo(() => {
      return Math.round(level / 5) * 5; // 5'li gruplarla throttle
    }, [level]);

    useEffect(() => {
      if (!svgRef.current) return;

      // Ses seviyesine göre ölçeklendirilmiş değer (throttle ile performans optimizasyonu)
      const normalizedLevel = Math.min(15, Math.max(5, throttledLevel * 0.5));

      // Çoklu dalga çizgileri oluştur
      const generateWaves = () => {
        const greenGradient = `
        <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style="stop-color: #34d399;" />
          <stop offset="50%" style="stop-color: #10b981;" />
          <stop offset="100%" style="stop-color: #059669;" />
        </linearGradient>
      `;

        const blueGradient = `
        <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style="stop-color: #60a5fa;" />
          <stop offset="50%" style="stop-color: #3b82f6;" />
          <stop offset="100%" style="stop-color: #2563eb;" />
        </linearGradient>
      `;

        const gradientDef = `
        <defs>
          ${color === "green" ? greenGradient : blueGradient}
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2 2" result="glow"/>
            <feMerge>
              <feMergeNode in="glow"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
      `;

        let waves = "";
        const waveCount = 5;
        const baseColor =
          color === "green" ? "rgba(16, 185, 129," : "rgba(59, 130, 246,";

        for (let i = 0; i < waveCount; i++) {
          const opacity = 1 - i * 0.15;
          const amplitude = normalizedLevel * (1 - i * 0.18);
          const frequency = 1 + i * 0.15;
          const phase = i * 25;
          const yOffset = 25;

          const path = `
          <path 
            d="M 0,${yOffset} ${generateSinePath(amplitude, frequency, phase)}"
            stroke="${
              i === 0 ? "url(#waveGradient)" : `${baseColor} ${opacity})`
            }"
            strokeWidth="${i === 0 ? 3 : 2}"
            strokeLinecap="round"
            fill="none"
            opacity="${opacity}"
            ${i === 0 ? 'filter="url(#glow)"' : ""}
          >
            <animate 
              attributeName="d" 
              values="M 0,${yOffset} ${generateSinePath(
            amplitude,
            frequency,
            phase
          )}; 
                      M 0,${yOffset} ${generateSinePath(
            amplitude,
            frequency,
            phase + 45
          )}; 
                      M 0,${yOffset} ${generateSinePath(
            amplitude,
            frequency,
            phase + 90
          )}; 
                      M 0,${yOffset} ${generateSinePath(
            amplitude,
            frequency,
            phase + 135
          )}; 
                      M 0,${yOffset} ${generateSinePath(
            amplitude,
            frequency,
            phase
          )}"
              dur="${3 - i * 0.4}s"
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0.4 0 0.6 1; 0.4 0 0.6 1; 0.4 0 0.6 1; 0.4 0 0.6 1"
            />
          </path>
        `;

          waves += path;
        }

        return gradientDef + waves;
      };

      const generateSinePath = (
        amplitude: number,
        frequency: number,
        phase: number
      ) => {
        const points = [];
        const steps = 100;

        for (let i = 0; i <= steps; i++) {
          const x = i * (100 / steps);
          const y = yCalculator(x, amplitude, frequency, phase);
          points.push(`${x},${y}`);
        }

        return points.join(" L ");
      };

      const yCalculator = (
        x: number,
        amplitude: number,
        frequency: number,
        phase: number
      ) => {
        const centerY = 25;
        return (
          centerY +
          amplitude *
            Math.sin((x * frequency * Math.PI) / 50 + (phase * Math.PI) / 180)
        );
      };

      if (svgRef.current) {
        svgRef.current.innerHTML = generateWaves();
      }
    }, [level, color]);

    return (
      <svg
        ref={svgRef}
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 50"
        preserveAspectRatio="xMidYMid meet"
      />
    );
  }
);

// Konuşma dalga animasyonu
const SpeakingWaveform = ({ color = "blue" }: { color?: "green" | "blue" }) => {
  const [level, setLevel] = useState<number>(8);

  useEffect(() => {
    let animationFrameId: number;
    let direction = 1;
    let currentLevel = 8;

    const animate = () => {
      if (direction > 0) {
        currentLevel += 0.2;
        if (currentLevel >= 12) {
          direction = -1;
        }
      } else {
        currentLevel -= 0.2;
        if (currentLevel <= 6) {
          direction = 1;
        }
      }

      setLevel(currentLevel);
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div className="relative w-full h-12 bg-gradient-to-r from-transparent via-blue-500/10 to-transparent rounded-lg overflow-hidden">
      <AudioWaveform level={level} color={color} />
    </div>
  );
};

// İşlem göstergesi
const ProcessingIndicator = () => {
  return (
    <div className="flex items-center justify-center space-x-2">
      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce"></div>
    </div>
  );
};

export const VoiceChat: React.FC = () => {
  const { t } = useTranslation();

  // ✅ Store-based hooks - NO prop drilling
  const {
    isVoiceChatActive,
    isVoiceChatVisible,
    isListening,
    isProcessing,
    isWaitingForResponse,
    isSpeaking,
    transcript,
    error,
    audioLevel,
    closeVoiceChat,
    minimizeVoiceChat,
    clearTranscript,
  } = useVoiceChat();

  // ✅ Chat entegrasyonu
  const { handleTranscriptSubmit } = useVoiceChatIntegration();

  if (!isVoiceChatActive || !isVoiceChatVisible) return null;

  const handleClose = () => {
    closeVoiceChat();
  };

  const handleMinimize = () => {
    minimizeVoiceChat();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeVoiceChat(); // ✅ Direct store action kullan
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeVoiceChat]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <div className="w-full max-w-md bg-background border border-border rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50">
            <h3 className="text-lg font-semibold text-foreground">
              {t("chat.voiceChat")}
            </h3>
            <div className="flex space-x-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleMinimize}
                className="h-8 w-8 hover:bg-accent"
                title={t("common.minimize")}
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8 hover:bg-red-500/20 hover:text-red-500"
                title={t("common.close")}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Durum Göstergesi */}
            <div className="text-center space-y-4">
              <div
                className={cn(
                  "text-sm font-medium",
                  isSpeaking
                    ? "text-blue-600"
                    : isListening
                    ? "text-green-600"
                    : isProcessing
                    ? "text-yellow-600"
                    : isWaitingForResponse
                    ? "text-purple-600"
                    : "text-muted-foreground"
                )}
              >
                {isSpeaking
                  ? t("chat.aiSpeaking")
                  : isListening
                  ? t("chat.speakNow")
                  : isProcessing
                  ? t("chat.processing")
                  : isWaitingForResponse
                  ? t("chat.waitingForResponse")
                  : t("chat.voiceChatReady")}
              </div>

              {/* Görsel İndikatör */}
              <div className="relative h-16 bg-muted rounded-lg overflow-hidden">
                {isSpeaking ? (
                  <SpeakingWaveform color="blue" />
                ) : isListening ? (
                  <AudioWaveform level={audioLevel} color="green" />
                ) : isProcessing ? (
                  <div className="flex items-center justify-center h-full">
                    <ProcessingIndicator />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="w-8 h-8 rounded-full bg-muted-foreground/20 flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full bg-muted-foreground/40"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Transkript */}
            {transcript && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  {t("chat.transcript")}:
                </label>
                <div className="p-3 bg-muted rounded-lg border">
                  <p className="text-sm text-foreground">{transcript}</p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    onClick={() => handleTranscriptSubmit()}
                    className="flex-1"
                    disabled={!transcript.trim()}
                  >
                    {t("chat.sendMessage")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearTranscript}
                  >
                    {t("common.clear")}
                  </Button>
                </div>
              </div>
            )}

            {/* Hata */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Yardım */}
            <div className="text-xs text-muted-foreground text-center space-y-1">
              <p>{t("chat.voiceChatInstructions")}</p>
              <p className="font-medium">ESC: {t("common.close")}</p>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
